import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowDownUp, RefreshCw, Trash2, CheckCircle, Clock, AlertTriangle, X,
  Wallet, Zap, Info, ExternalLink, Lock, Shield, ChevronDown, ChevronUp, Copy,
} from 'lucide-react';
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Spinner, InfoTip } from '../components/ui';
import {
  prepareDexLimitOrder,
  submitDexLimitOrder,
  listDexLimitOrders,
  cancelDexLimitOrder,
  refreshDexLimitOrder,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor, chartPairFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';
import TradingViewChart from '../components/TradingViewChart';

// Standard ERC20 ABI fragments — allowance + approve only
const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];

const MAX_UINT256 = 2n ** 256n - 1n;

// CoW VaultRelayer — same address all chains. Velora's Delta contract is
// chain-specific; we fetch it from /prepare's response per-order.
const COW_SPENDER = '0xC92E8bdf79f0507f65a392b0ab4667716BFE0110';
// Spender resolution: CoW is constant, Velora comes from backend prepare()
// (we stash it in component state when the protocol is velora).
function spenderForProtocol(p, veloraSpender) {
  if (p === 'cow') return COW_SPENDER;
  if (p === 'velora') return veloraSpender;
  return null;
}

const PROTOCOL_INFO = {
  'cow': {
    label: 'CoW Protocol',
    badge: 'No KYC · No key',
    badgeTone: 'emerald',
    blurb: 'Solver auctions with MEV protection + surplus refunds. Ethereum, Arbitrum, Base.',
  },
  'velora': {
    label: 'Velora',
    badge: 'No KYC · No key',
    badgeTone: 'emerald',
    blurb: 'EU-based intent-based fills (formerly ParaSwap). Adds Polygon, BSC, Optimism, Avalanche.',
  },
};

const EXPIRY_OPTIONS = [
  { label: '24 hours', sec: 86400 },
  { label: '7 days',   sec: 7 * 86400 },
  { label: '30 days',  sec: 30 * 86400 },
  { label: '90 days',  sec: 90 * 86400 },
];

// Status badge styling
const STATUS_STYLE = {
  open:      { label: 'Open',          cls: 'bg-emerald-500/10 text-emerald-400', icon: Clock },
  partial:   { label: 'Partial fill',  cls: 'bg-blue-500/10 text-blue-300',       icon: Zap },
  filled:    { label: 'Filled',        cls: 'bg-emerald-500/15 text-emerald-300', icon: CheckCircle },
  cancelled: { label: 'Cancelled',     cls: 'bg-dark-600 text-gray-400',          icon: X },
  expired:   { label: 'Expired',       cls: 'bg-amber-500/10 text-amber-400',     icon: AlertTriangle },
};

function shortenAddr(a) { if (!a) return '—'; return `${a.slice(0, 6)}…${a.slice(-4)}`; }
function fmtTime(iso)   { if (!iso) return '—'; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

// Convert a human-input decimal string to a wei-equivalent integer string for
// a token with `decimals` precision. Pure-string math — no float rounding.
function toBaseUnits(humanAmount, decimals) {
  if (humanAmount == null || humanAmount === '') throw new Error('amount required');
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error(`amount must be a non-negative decimal, got "${s}"`);
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}

function formatBaseUnits(baseStr, decimals, maxFrac = 6) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.open;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

// ─── Create-order panel ──────────────────────────────────────────────────────
function CreateOrderPanel({ onCreated, onToast, onPairChange }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync, isPending: signing } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [protocol, setProtocol] = useState('cow');         // default to CoW — works without API key
  const [chainId, setChainId]   = useState(connectedChainId || 1);
  const [sellSym, setSellSym]   = useState('WETH');
  const [buySym,  setBuySym]    = useState('USDC');
  const [sellAmount, setSellAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState(''); // sell-token priced in buy-token
  const [expirySec,  setExpirySec]  = useState(7 * 86400);
  const [submitting, setSubmitting] = useState(false);
  const [stage,      setStage]      = useState(null); // 'checking-allowance' | 'approving' | 'signing' | 'submitting'
  const [customTick, setCustomTick] = useState(0);    // bump to re-pull tokens after a custom add

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const availableChains = CHAINS.filter(c => PROTOCOL_CHAINS[protocol].includes(c.id));

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  // Bubble token selection up so the page-level chart can follow it
  useEffect(() => {
    onPairChange?.({ sellSym, buySym, chainId });
  }, [sellSym, buySym, chainId, onPairChange]);

  // If user switches to a protocol that doesn't support the current chain,
  // bounce them to the first supported chain for that protocol.
  useEffect(() => {
    if (!PROTOCOL_CHAINS[protocol].includes(chainId)) {
      setChainId(PROTOCOL_CHAINS[protocol][0]);
    }
  }, [protocol, chainId]);

  // Live preview of how much buy-token they receive at the limit price.
  const buyAmountPreview = useMemo(() => {
    const a = Number(sellAmount), p = Number(limitPrice);
    if (!Number.isFinite(a) || !Number.isFinite(p) || a <= 0 || p <= 0) return null;
    return a * p;
  }, [sellAmount, limitPrice]);

  const canSubmit =
    isConnected && connectedAddr &&
    Number(connectedChainId) === Number(chainId) &&
    sellToken && buyToken && sellToken.symbol !== buyToken.symbol &&
    Number(sellAmount) > 0 && Number(limitPrice) > 0 &&
    !signing && !submitting;

  async function ensureAllowance(spender, requiredAmount) {
    // Polls allowance on-chain. If insufficient, fires an unlimited
    // approve() and waits until the on-chain allowance reflects it. We poll
    // allowance directly rather than wait on a tx hash — more reliable
    // across RPC providers that drop the receipt before it propagates.
    setStage('checking-allowance');
    async function readAllowance() {
      return publicClient.readContract({
        address: sellToken.address,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [connectedAddr, spender],
      });
    }
    let allowance = await readAllowance();
    if (allowance >= requiredAmount) return;

    // USDT and a few non-standard ERC-20s revert if approve() is called while
    // current allowance is non-zero. Reset to zero first if needed.
    if (allowance > 0n) {
      setStage('approving');
      onToast({ type: 'info', message: `${sellSym} needs its old allowance reset to zero. Confirm in your wallet (Signature 1/3).` });
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      // Poll until reset
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await readAllowance(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error(`${sellSym} allowance reset didn't confirm in 3 minutes.`);
    }

    setStage('approving');
    onToast({ type: 'info', message: `First-time ${sellSym} on ${PROTOCOL_INFO[protocol].label} — approve once in your wallet, then sign the order. ~15-30s for on-chain confirmation.` });
    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    // Poll until the new allowance is in effect
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await readAllowance(); } catch {}
      if (allowance >= requiredAmount) return;
    }
    throw new Error(`${sellSym} approval didn't confirm in 3 minutes. Check your wallet's Activity tab; if it shows Confirmed there, click Create again.`);
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Convert UI inputs to base-unit integers
      const makingAmount = toBaseUnits(sellAmount, sellToken.decimals);
      const takingHuman  = Number(sellAmount) * Number(limitPrice);
      const takingAmount = toBaseUnits(takingHuman.toString(), buyToken.decimals);
      const expiry       = Math.floor(Date.now() / 1000) + Number(expirySec);

      // 1. Prepare on backend FIRST. For Velora we need the spender from the
      // backend response since it's chain-specific; for CoW it's constant.
      setStage('signing');
      const prep = await prepareDexLimitOrder({
        protocol, chainId,
        maker: connectedAddr,
        makerAsset: sellToken.address,
        takerAsset: buyToken.address,
        makingAmount,
        takingAmount,
        expirySec: expiry,
        // Velora needs decimals to call its quote endpoint correctly
        srcDecimals:  sellToken.decimals,
        destDecimals: buyToken.decimals,
      });
      const { typedData, order } = prep.data;
      const spender = prep.data.spender || spenderForProtocol(protocol);

      // 2. ERC20 approval — using whichever spender this protocol asked for
      await ensureAllowance(spender, BigInt(makingAmount));

      // 3. Sign EIP-712 with wagmi — opens MetaMask signing dialog
      setStage('signing');
      const signature = await signTypedDataAsync({
        domain: typedData.domain,
        types: typedData.types,
        primaryType: typedData.primaryType,
        message: order,
      });

      // 4. Submit to backend — verifies signature, posts to chosen orderbook
      setStage('submitting');
      const result = await submitDexLimitOrder({
        protocol, chainId, order, signature,
        owner: connectedAddr,
        makerSymbol: sellToken.symbol, takerSymbol: buyToken.symbol,
        makerDecimals: sellToken.decimals, takerDecimals: buyToken.decimals,
        limitPrice: String(limitPrice),
      });

      onToast({ type: 'success', message: `Order posted to ${PROTOCOL_INFO[protocol].label}. Fills at ${limitPrice} ${buyToken.symbol}/${sellToken.symbol}.` });
      setSellAmount(''); setLimitPrice('');
      onCreated(result.data?.record);
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Could not create order';
      onToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
      setStage(null);
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center">
          <ArrowDownUp className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New DEX Limit Order</h3>
          <p className="text-gray-500 text-xs">Signed once. Filled automatically by {PROTOCOL_INFO[protocol]?.label || 'protocol'} solvers when your price hits.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
              <p className="text-amber-300/80 text-xs mt-0.5">We'll never ask for your private key — only EIP-712 signatures per order.</p>
            </div>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 transition-all flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}

      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">
              Your wallet is on chain <span className="font-semibold">{CHAINS.find(c => c.id === Number(connectedChainId))?.label || `#${connectedChainId}`}</span>.
              This order needs to be signed on <span className="font-semibold">{CHAINS.find(c => c.id === Number(chainId))?.label}</span>.
            </p>
          </div>
          <button onClick={() => switchChain?.({ chainId: Number(chainId) })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-40">
            {switchingChain ? <Spinner size="sm" /> : <Info className="w-4 h-4" />}
            {switchingChain ? 'Switching…' : `Switch wallet to ${CHAINS.find(c => c.id === Number(chainId))?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Protocol selector — CoW first (free, no API key, no KYC) */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Protocol
            <InfoTip side="right">CoW = Ethereum / Arbitrum / Base, includes surplus refunds. Velora = adds Polygon / BSC / Optimism / Avalanche. Both are gas-free at fill time and need no API key.</InfoTip>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['cow', 'velora'].map(p => {
              const info = PROTOCOL_INFO[p];
              const tone = info.badgeTone === 'emerald' ? 'text-emerald-400 bg-emerald-500/10' : 'text-amber-400 bg-amber-500/10';
              return (
                <button key={p} onClick={() => setProtocol(p)}
                  className={`px-3 py-2.5 rounded-xl text-left text-sm transition-colors border ${
                    protocol === p ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{info.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${tone}`}>{info.badge}</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">{info.blurb}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chain — filtered by protocol */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {availableChains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          {protocol === 'cow' && (
            <p className="text-gray-500 text-[10px] mt-1">CoW does not support Polygon — switch to 1inch for Polygon orders.</p>
          )}
        </div>

        {/* Sell side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">You sell</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}{t.custom ? ' (custom)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Amount</label>
            <input type="number" min="0" step="any" value={sellAmount} onChange={e => setSellAmount(e.target.value)}
              placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
        </div>

        {/* Buy side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">You receive</label>
            <select value={buySym} onChange={e => setBuySym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}{t.custom ? ' (custom)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Limit price <span className="text-gray-600">({buySym}/{sellSym})</span>
              <InfoTip side="left">Price of 1 {sellSym} in {buySym}. For SELLS, set this ABOVE current market (you only sell when {sellSym} rises). The preview below shows what you receive at fill.</InfoTip>
            </label>
            <input type="number" min="0" step="any" value={limitPrice} onChange={e => setLimitPrice(e.target.value)}
              placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
        </div>

        {/* Custom-token resolver — paste any ERC-20 */}
        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {buyAmountPreview != null && (
          <div className="bg-dark-700/40 rounded-xl px-4 py-2.5 text-xs">
            <span className="text-gray-500">You'll receive ≥ </span>
            <span className="text-white font-semibold">{buyAmountPreview.toLocaleString(undefined, { maximumFractionDigits: 6 })} {buySym}</span>
            <span className="text-gray-500"> when filled</span>
          </div>
        )}

        {/* Expiry */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Order expires after
            <InfoTip side="right">Auto-cancel if not filled by this point. Tight targets → short expiry (24h-7d). Distant targets → 30d-90d. You can cancel manually at any time for free on CoW.</InfoTip>
          </label>
          <div className="grid grid-cols-4 gap-2">
            {EXPIRY_OPTIONS.map(opt => (
              <button key={opt.sec} onClick={() => setExpirySec(opt.sec)}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                  expirySec === opt.sec ? 'bg-accent-primary text-dark-900' : 'bg-dark-700 text-gray-400 hover:bg-dark-600'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button onClick={handleSubmit} disabled={!canSubmit}
          className="w-full btn-primary py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
          {(submitting || signing) ? <Spinner size="sm" /> : <Zap className="w-4 h-4" />}
          {stage === 'checking-allowance' ? 'Checking allowance…' :
           stage === 'approving' ? `Approving ${sellSym}…` :
           stage === 'signing' || signing ? 'Waiting for wallet signature…' :
           stage === 'submitting' ? `Submitting to ${PROTOCOL_INFO[protocol].label}…` :
           'Create limit order'}
        </button>
        <p className="text-gray-500 text-[11px] text-center flex items-center justify-center gap-1">
          First-time {sellSym}? You'll approve it once (small one-time gas), then sign the order. Fills are gas-free for you.
          <InfoTip side="top">Solvers absorb the fill gas in exchange for the price improvement they extract. CoW returns surplus to you; Velora keeps it for the solver.</InfoTip>
        </p>
      </div>
    </Card>
  );
}

// CoW chain → explorer subdomain
const COW_EXPLORER = {
  1: 'mainnet', 42161: 'arbitrum_one', 8453: 'base', 100: 'gnosis',
};
function externalLinkFor(record) {
  const protocol = record.protocol || 'cow';
  const id = record.orderId || record.orderHash;
  if (protocol === 'cow') {
    const chain = COW_EXPLORER[record.chainId];
    return chain ? `https://explorer.cow.fi/${chain}/orders/${id}` : null;
  }
  if (protocol === 'velora') {
    // Velora doesn't have a public per-order explorer; link to their app for now
    return `https://app.velora.xyz/`;
  }
  return `https://app.1inch.io/#/${record.chainId}/limit-order/order/${id}`;
}

// ─── Order row ───────────────────────────────────────────────────────────────
function OrderRow({ record, onCancel, onRefresh, busy }) {
  const [expanded, setExpanded] = useState(false);
  const filledBase = (BigInt(record.makingAmount) - BigInt(record.remainingMakerAmount || record.makingAmount)).toString();
  const filledPct = record.remainingMakerAmount && record.makingAmount
    ? Math.min(100, Math.round(100 * Number(BigInt(filledBase) * 10000n / BigInt(record.makingAmount)) / 100))
    : 0;

  const sellHuman = formatBaseUnits(record.makingAmount, record.makerDecimals || 18);
  const buyHuman  = formatBaseUnits(record.takingAmount, record.takerDecimals || 18);
  const filledHuman = formatBaseUnits(filledBase, record.makerDecimals || 18);
  const protocol  = record.protocol || 'cow';
  const protoLabel = protocol === 'cow' ? 'CoW' : protocol === 'velora' ? 'Velora' : '1inch';
  const explorerHref = externalLinkFor(record);
  const orderRef = record.orderId || record.orderHash;

  function copyToClipboard(text) {
    try { navigator.clipboard?.writeText(text); } catch {}
  }

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white text-sm font-semibold">
                {sellHuman} {record.makerSymbol || '—'} → {buyHuman} {record.takerSymbol || '—'}
              </span>
              <StatusBadge status={record.status} />
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">{protoLabel}</span>
            </div>
            <p className="text-gray-500 text-xs mt-0.5">
              {record.limitPrice ? `Limit ${record.limitPrice} ${record.takerSymbol}/${record.makerSymbol} · ` : ''}
              Chain {record.chainId} · Created {fmtTime(record.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {record.status === 'open' || record.status === 'partial' ? (
              <>
                <button onClick={() => onRefresh(orderRef)} disabled={busy}
                  title={`Re-poll status from ${protoLabel}`}
                  className="p-1.5 rounded-lg bg-dark-700 text-gray-400 hover:text-white hover:bg-dark-600 disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => onCancel(orderRef)} disabled={busy}
                  title="Soft cancel (stops UI tracking)"
                  className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            ) : null}
            {explorerHref && (
              <a href={explorerHref} target="_blank" rel="noreferrer"
                 className="p-1.5 rounded-lg bg-dark-700 text-gray-400 hover:text-white hover:bg-dark-600"
                 title={`View on ${protoLabel}`}>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <span className="ml-1 text-gray-500" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
        {filledPct > 0 && filledPct < 100 && (
          <div className="mt-1">
            <div className="h-1 rounded-full bg-dark-600 overflow-hidden">
              <div className="h-full bg-blue-400" style={{ width: `${filledPct}%` }} />
            </div>
            <p className="text-gray-500 text-[10px] mt-1">{filledPct}% filled</p>
          </div>
        )}
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[lo-fade_0.2s_ease-out]">
          <style>{`@keyframes lo-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          {/* Order ID */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Order ID</div>
            <button onClick={() => copyToClipboard(orderRef)}
              title="Copy"
              className="text-xs font-mono text-white break-all hover:text-accent-primary transition-colors text-left">
              {orderRef}
            </button>
          </div>
          {/* Amounts breakdown */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Sell (full)</div>
              <div className="text-xs text-white font-mono">{sellHuman} {record.makerSymbol}</div>
              <div className="text-[10px] text-gray-500 font-mono mt-0.5 truncate" title={record.makingAmount}>{record.makingAmount} base</div>
            </div>
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Buy target</div>
              <div className="text-xs text-white font-mono">{buyHuman} {record.takerSymbol}</div>
              <div className="text-[10px] text-gray-500 font-mono mt-0.5 truncate" title={record.takingAmount}>{record.takingAmount} base</div>
            </div>
          </div>
          {(record.status === 'partial' || filledPct > 0) && (
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Filled so far</div>
              <div className="text-xs text-blue-300 font-mono">{filledHuman} {record.makerSymbol} ({filledPct}%)</div>
            </div>
          )}
          {/* Lifecycle timeline */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Lifecycle</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-white">{fmtTime(record.createdAt)}</span></div>
              {record.filledAt && (
                <div className="flex justify-between"><span className="text-emerald-400">Filled</span><span className="text-white">{fmtTime(record.filledAt)}</span></div>
              )}
              {record.cancelledAt && (
                <div className="flex justify-between"><span className="text-red-400">Cancelled</span><span className="text-white">{fmtTime(record.cancelledAt)}</span></div>
              )}
              {record.updatedAt && (
                <div className="flex justify-between"><span className="text-gray-500">Last update</span><span className="text-gray-400">{fmtTime(record.updatedAt)}</span></div>
              )}
            </div>
          </div>
          {record.failedReason && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-red-400 font-semibold mb-1">Failure reason</div>
              <p className="text-xs text-red-300">{record.failedReason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function DexLimitOrdersPage() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [busy, setBusy]       = useState(null); // orderHash being mutated
  const [chartPair, setChartPair] = useState('ETH/USDT');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDexLimitOrders();
      setOrders(r.data?.orders || []);
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Could not load orders' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleCreated = (record) => {
    if (record) setOrders(prev => [record, ...prev.filter(o => o.orderHash !== record.orderHash)]);
  };

  const handleCancel = async (orderHash) => {
    setBusy(orderHash);
    try {
      const r = await cancelDexLimitOrder(orderHash);
      setOrders(prev => prev.map(o => o.orderHash === orderHash ? r.data?.record || o : o));
      setToast({ type: 'success', message: 'Order cancelled in your account. For on-chain cancellation, see 1inch.' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Cancel failed' });
    } finally {
      setBusy(null);
    }
  };

  const handleRefresh = async (orderHash) => {
    setBusy(orderHash);
    try {
      const r = await refreshDexLimitOrder(orderHash);
      setOrders(prev => prev.map(o => o.orderHash === orderHash ? r.data?.record || o : o));
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Refresh failed' });
    } finally {
      setBusy(null);
    }
  };

  const active = orders.filter(o => o.status === 'open' || o.status === 'partial');
  const history = orders.filter(o => !['open','partial'].includes(o.status));

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-accent-primary/15 text-accent-primary flex items-center justify-center">
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX Limit Orders</h1>
            <p className="text-gray-400 text-sm">Real auto-fill limit orders via 1inch resolvers. Sign once, fills automatically when your price hits.</p>
          </div>
        </div>
      </div>

      {/* TradingView chart — follows your sell/buy selection */}
      <Card className="!p-3">
        <div className="flex items-center justify-between mb-2 px-2 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs uppercase tracking-wide">Reference chart</span>
            <span className="text-white text-sm font-mono">{chartPair}</span>
          </div>
          <span className="text-[10px] text-gray-500">Powered by TradingView · price reference only</span>
        </div>
        <TradingViewChart symbol={chartPair} interval="60" height={320} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateOrderPanel onCreated={handleCreated} onToast={setToast}
          onPairChange={({ sellSym, buySym }) => setChartPair(chartPairFor(sellSym, buySym))} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Active orders</h3>
              <p className="text-gray-500 text-xs">{active.length} open · auto-refreshes every 2 min</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Clock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No active orders. Create one to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(o => (
                <OrderRow key={o.orderHash} record={o}
                  onCancel={handleCancel} onRefresh={handleRefresh}
                  busy={busy === o.orderHash} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {history.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">History</h3>
          <div className="space-y-2">
            {history.map(o => (
              <OrderRow key={o.orderHash} record={o}
                onCancel={handleCancel} onRefresh={handleRefresh}
                busy={busy === o.orderHash} />
            ))}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
